import { ConnectedRouter } from 'connected-react-router';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { ApolloProvider } from 'react-apollo';
import { Provider, ReactReduxContext } from 'react-redux';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Provider as UnstatedProvider } from 'unstated';

import { ConnectSupport } from '@confluence/connect-support';
import { ConfluenceAnalyticsListener } from '@confluence/analytics';
import { getBrowserHistory } from '@confluence/browser-history';
import { UpFlowProvider } from '@confluence/change-edition/entry-points/UpFlowProvider';
import { Dialogs } from '@confluence/dialogs';
import {
	ExperienceTrackerContext,
	getExperienceTracker, // eslint-disable-line no-restricted-imports
} from '@confluence/experience-tracker';
import { Flags } from '@confluence/flags';
import { getApolloClient } from '@confluence/graphql';
import { I18nProvider } from '@confluence/i18n';
import { namedRoutes } from '@confluence/named-routes';
import { NavdexListener } from '@confluence/navdex';
import { RouteManager } from '@confluence/route-manager/entry-points/RouteManager';
import { SPAViewContextProvider } from '@confluence/spa-view-context';
import { getStandalonePageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container/entry-points/getStandalonePageTreeStateUpdater';

import { EngagementProvider } from '../EngagementProvider';

/**
 * This component is responsible for bootstrapping navigation for legacy server rendered page.
 * Also it bootstraps React Component in legacy location like inline comment body.
 */
export default class ComponentWrapper extends Component {
	static propTypes = {
		/**
		 * Global store
		 */
		store: PropTypes.object.isRequired,
		/**
		 * Context including the css handler (It has different versions for client and server rendering)
		 */
		context: PropTypes.shape({
			insertCss: PropTypes.func,
		}),
		/**
		 * Children Component
		 */
		children: PropTypes.any,
		/**
		 * Indicates if the component is rendered as Root
		 */
		renderAsRoot: PropTypes.bool,
	};

	static childContextTypes = {
		/**
		 * Handler for css injection in components.
		 */
		insertCss: PropTypes.func.isRequired,
	};

	getChildContext() {
		const { context } = this.props;
		const noop = () => {};
		return {
			insertCss: context.insertCss || noop,
		};
	}

	browserHistory = getBrowserHistory();

	render() {
		const { store } = this.props;
		return (
			<UnstatedProvider inject={[getStandalonePageTreeStateUpdater()]}>
				<ApolloProvider client={getApolloClient()}>
					<Provider store={store}>
						<SPAViewContextProvider>
							<ExperienceTrackerContext.Provider value={getExperienceTracker()}>
								<I18nProvider>
									<ConfluenceAnalyticsListener apolloClient={getApolloClient()}>
										<NavdexListener>
											<EngagementProvider>
												<ConnectedRouter history={this.browserHistory} context={ReactReduxContext}>
													<UpFlowProvider>
														<Flags />
														<ConnectSupport />
														<RouteManager namedRoutes={namedRoutes}>
															<Dialogs />
															{this.props.children}
														</RouteManager>
													</UpFlowProvider>
												</ConnectedRouter>
											</EngagementProvider>
										</NavdexListener>
									</ConfluenceAnalyticsListener>
								</I18nProvider>
							</ExperienceTrackerContext.Provider>
						</SPAViewContextProvider>
					</Provider>
				</ApolloProvider>
			</UnstatedProvider>
		);
	}
}
